import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import './Profile.css';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState(null);
  const [delStep,  setDelStep]  = useState(0); // 0 = normal, 1 = primera confirmación, 2 = segunda confirmación
  const [deleting, setDeleting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/me');
      const profileData = res.data.user || res.data;
      setProfile(profileData);
    } catch {
      setProfile(user);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  async function handleDeleteAccount() {
    if (delStep < 2) {
      setDelStep(s => s + 1);
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/auth/account');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al eliminar la cuenta');
      setDelStep(0);
    } finally {
      setDeleting(false);
    }
  }

  const displayUser = profile || user;

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <div className="page-title">Perfil</div>
        </div>

        {loading ? (
          <div className="px-16">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 0 24px' }}>
              <div className="skeleton skeleton-avatar" />
              <div style={{ flex: 1 }}>
                <div className="skeleton skeleton-line" style={{ width: '50%' }} />
                <div className="skeleton skeleton-line-sm" />
              </div>
            </div>
            <div className="skeleton skeleton-card-lg" />
          </div>
        ) : (
          <div className="px-16">
            <div className="profile-avatar-section">
              <div className="profile-avatar">
                {(displayUser?.username || 'U')[0].toUpperCase()}
              </div>
              <div>
                <div className="profile-username">{displayUser?.username}</div>
                <div className="profile-email text-muted text-sm">{displayUser?.email}</div>
              </div>
            </div>

            {profile?.stats && (
              <div className="card mb-16">
                <h3 className="mb-12">Mis estadísticas</h3>
                <div className="profile-stats-grid">
                  <div className="profile-stat">
                    <div className="stat-value">{profile.stats.total_workouts ?? 0}</div>
                    <div className="stat-label">Entrenamientos</div>
                  </div>
                  <div className="profile-stat">
                    <div className="stat-value">{profile.stats.current_streak ?? 0}🔥</div>
                    <div className="stat-label">Racha</div>
                  </div>
                  <div className="profile-stat">
                    <div className="stat-value">{profile.stats.total_exercises ?? 0}</div>
                    <div className="stat-label">Ejercicios</div>
                  </div>
                </div>
              </div>
            )}

            <button
              className="btn btn-danger btn-full logout-btn"
              onClick={handleLogout}
              type="button"
            >
              🚪 Cerrar sesión
            </button>

            {delStep === 0 && (
              <button
                className="btn btn-outline-danger btn-full mt-8"
                onClick={() => setDelStep(1)}
                type="button"
              >
                🗑️ Eliminar cuenta
              </button>
            )}

            {delStep === 1 && (
              <div className="delete-confirm-box mt-8">
                <p className="delete-confirm-text">¿Estás seguro? Se eliminarán todos tus datos, rutinas e historial de entrenamiento.</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setDelStep(0)} type="button">
                    Cancelar
                  </button>
                  <button className="btn btn-danger" style={{ flex: 1 }} onClick={() => setDelStep(2)} type="button">
                    Sí, continuar
                  </button>
                </div>
              </div>
            )}

            {delStep === 2 && (
              <div className="delete-confirm-box delete-confirm-box--final mt-8">
                <p className="delete-confirm-text">⚠️ Última oportunidad. Esta acción es irreversible. ¿Confirmas que quieres eliminar tu cuenta permanentemente?</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setDelStep(0)} type="button">Cancelar</button>
                  <button className="btn btn-danger" style={{ flex: 1 }} onClick={handleDeleteAccount} disabled={deleting} type="button">
                    {deleting ? <span className="spinner spinner-sm" /> : 'Eliminar definitivamente'}
                  </button>
                </div>
              </div>
            )}

            <button
              className="btn btn-secondary btn-full mt-8"
              onClick={() => setShowHelp(s => !s)}
              type="button"
            >
              {showHelp ? '❓ Ocultar ayuda' : '❓ Ayuda'}
            </button>

            {showHelp && (
              <div className="card mb-8" style={{ marginTop: 12 }}>
                <h3 className="mb-12 fw-700">Cómo usar GYM Tracker</h3>

                <div className="help-section">
                  <h4>🏋️ Hoy</h4>
                  <p>Tu entrenamiento del día. Marca las series como hechas, anota peso y repeticiones. Usa los tabs de días para cargar la rutina de otro día. Puedes añadir ejercicios sueltos con el botón "+ Agregar ejercicio".</p>
                </div>

                <div className="help-section">
                  <h4>💪 Ejercicios</h4>
                  <p>Biblioteca de ejercicios. Filtra por categoría, ordena por nombre/uso/PR. Los ejercicios marcados como <span className="badge badge-admin" style={{ fontSize: '0.65rem' }}>Admin</span> son creados por el administrador y están disponibles para todos.</p>
                </div>

                <div className="help-section">
                  <h4>📅 Rutinas</h4>
                  <p>Planifica tu semana. Selecciona un día, añade ejercicios de la biblioteca, configura series, repeticiones y descanso. Puedes copiar rutinas entre días o mover ejercicios con el botón ↗.</p>
                </div>

                <div className="help-section">
                  <h4>📊 Historial</h4>
                  <p>Revisa tus entrenamientos pasados. Usa las flechas ◀ ▶ para navegar entre semanas. Alterna entre vista lista y gráfico de volumen mensual.</p>
                </div>

                <div className="help-section">
                  <h4>🏆 Récords personales</h4>
                  <p>Al entrar en un ejercicio desde la biblioteca, verás tu historial y tu récord personal. El PR se actualiza automáticamente al superar tu mejor peso o repeticiones.</p>
                </div>

                <div className="help-section">
                  <h4>❤️ Cardio</h4>
                  <p>Los ejercicios de cardio solo necesitan tiempo (minutos). No tienen series ni peso.</p>
                </div>

                <div className="help-section">
                  <h4>🔄 Sin peso</h4>
                  <p>Ejercicios como flexiones o dominadas no necesitan peso. Solo anotas las repeticiones.</p>
                </div>
              </div>
            )}

            <div className="app-version text-muted text-xs">
              GYM Tracker v1.0.0
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
